/**
 * useCountMode — MADE-292
 *
 * localStorage helpers for Count Mode state.
 * Key: `os_count_mode`
 *
 * Written when a count session QR is scanned and verified.
 * Cleared on Pause or Abandon.
 * On app restart, verified via GET /api/os/count-sessions/:sessionId/progress.
 */

import { useCallback, useSyncExternalStore } from 'react';

export const COUNT_MODE_KEY = 'os_count_mode';

export interface CountModeState {
  sessionId: string;
  sessionName: string;
  location: string;
  countedItems: number;
  totalItems: number;
  rosterMode?: 'open' | 'fixed';
  enteredAt: string; // ISO timestamp
}

const CHANGE_EVENT = 'os-count-mode-change';
const notify = () => window.dispatchEvent(new Event(CHANGE_EVENT));
const subscribe = (listener: () => void) => {
  window.addEventListener(CHANGE_EVENT, listener);
  window.addEventListener('storage', listener);
  return () => {
    window.removeEventListener(CHANGE_EVENT, listener);
    window.removeEventListener('storage', listener);
  };
};
const readStoredValue = () => {
  try { return localStorage.getItem(COUNT_MODE_KEY); } catch { return null; }
};

export function useCountMode() {
  // Every mounted consumer refreshes when this device or another tab updates progress.
  useSyncExternalStore(subscribe, readStoredValue, () => null);
  /** Read the current count mode state from localStorage. Returns null if absent or malformed. */
  const getCountMode = useCallback((): CountModeState | null => {
    try {
      const raw = localStorage.getItem(COUNT_MODE_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as CountModeState;
    } catch {
      return null;
    }
  }, []);

  /** Write (or overwrite) count mode state to localStorage. */
  const setCountMode = useCallback((state: CountModeState) => {
    localStorage.setItem(COUNT_MODE_KEY, JSON.stringify(state));
    notify();
  }, []);

  /** Remove count mode state (Pause or Abandon). */
  const clearCountMode = useCallback(() => {
    localStorage.removeItem(COUNT_MODE_KEY);
    notify();
  }, []);

  /**
   * Update only the `countedItems` field in the stored state.
   * No-op if nothing is stored.
   */
  const updateCountedItems = useCallback((countedItems: number, totalItems?: number, sessionId?: string, rosterMode?: 'open' | 'fixed') => {
    if (!Number.isFinite(countedItems)) return;
    try {
      const raw = localStorage.getItem(COUNT_MODE_KEY);
      if (!raw) return;
      const state = JSON.parse(raw) as CountModeState;
      if (sessionId && state.sessionId !== sessionId) return;
      localStorage.setItem(COUNT_MODE_KEY, JSON.stringify({
        ...state, countedItems,
        ...(rosterMode ? { rosterMode } : {}),
        ...(totalItems !== undefined && Number.isFinite(totalItems) ? { totalItems } : {}),
      }));
      notify();
    } catch {
      // ignore parse errors
    }
  }, []);

  return { getCountMode, setCountMode, clearCountMode, updateCountedItems };
}
